import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React, { useContext, useRef, useState } from 'react';
import { TabsContext } from "./TabsContext";
import styled from 'styled-components';
import { getTabId, getPanelId } from "./get-accessibility-ids";
import { theme, whenFocused } from "../../theme";
import { useLazyLoad } from "./useLazyLoad";
import { TabTransitionStatusProvider } from "./useTabTransitionStatus";
import { validChildren } from "../../utils/valid-children";
import { Box } from "../Box/Box";
export function TabContent(_ref) {
  var children = _ref.children,
      _ref$lazyLoad = _ref.lazyLoad,
      lazyLoad = _ref$lazyLoad === void 0 ? true : _ref$lazyLoad,
      ariaLabelledById = _ref['aria-labelledby'],
      other = _objectWithoutProperties(_ref, ["children", "lazyLoad", "aria-labelledby"]);

  var _useContext = useContext(TabsContext),
      id = _useContext.id,
      currentPage = _useContext.value,
      mountedTabs = _useContext.mountedTabs,
      onMountTabs = _useContext.onMountTabs;

  var _useLazyLoad = useLazyLoad({
    lazyLoad: lazyLoad,
    itemCount: validChildren(children).length,
    currentPage: currentPage,
    loaded: mountedTabs,
    onRequestChange: onMountTabs
  }),
      shouldLoadIndex = _useLazyLoad.shouldLoadIndex;

  return /*#__PURE__*/React.createElement(Box, _extends({
    position: "relative",
    overflow: "hidden"
  }, other), React.Children.map(children, function (child, i) {
    var shouldLoad = shouldLoadIndex(i);
    return /*#__PURE__*/React.createElement(TabTransitions, {
      key: i,
      shouldLoad: shouldLoad
    }, function (_ref2) {
      var ref = _ref2.ref,
          onTransitionEnd = _ref2.onTransitionEnd;
      return /*#__PURE__*/React.createElement(TabPanel, {
        key: i,
        role: "tabpanel",
        "aria-labelledby": ariaLabelledById || (id ? getTabId(id, i) : undefined),
        id: id ? getPanelId(id, i) : undefined,
        "aria-hidden": currentPage !== i,
        style: getStyleForIndex(currentPage, i),
        tabIndex: currentPage === i ? 0 : -1,
        ref: ref,
        onTransitionEnd: onTransitionEnd
      }, shouldLoad && child);
    });
  }));
}
/**
 * Transition helper
 */

function TabTransitions(_ref3) {
  var children = _ref3.children,
      shouldLoad = _ref3.shouldLoad;
  var tabPanel = useRef(null);

  var _useState = useState(shouldLoad),
      _useState2 = _slicedToArray(_useState, 2),
      shouldRenderContent = _useState2[0],
      setShouldRenderContent = _useState2[1];

  function handleTransitionEnd(info) {
    if (!shouldRenderContent && info.propertyName === 'transform' && info.target === tabPanel.current) {
      setShouldRenderContent(true);
    }
  }

  return /*#__PURE__*/React.createElement(TabTransitionStatusProvider, {
    mountTransitionComplete: shouldRenderContent
  }, children({
    ref: tabPanel,
    onTransitionEnd: handleTransitionEnd
  }));
}
/**
 * Styles
 */


var TabPanel = styled.div.withConfig({
  displayName: "TabContent__TabPanel",
  componentId: "vapor__sc-72mry8-0"
})(["position:absolute;top:0;left:0;bottom:0;right:0;transition-property:opacity,transform,visibility;transition-duration:150ms;transition-timing-function:", ";outline:none;", "{box-shadow:", ";}"], theme.transitions.eases.primary, whenFocused, theme.shadows.interactive.focus);

function getStyleForIndex(currentPage, i) {
  if (currentPage > i) {
    return {
      transform: "translateX(-".concat(theme.spaces[16], ")"),
      opacity: 0,
      pointerEvents: 'none',
      transitionDelay: '0s',
      zIndex: 0,
      visibility: 'hidden'
    };
  }

  if (currentPage === i) {
    return {
      transform: 'translateX(0)',
      opacity: 1,
      pointerEvents: 'inherit',
      transitionDelay: '50ms',
      zIndex: 1,
      visibility: 'visible'
    };
  }

  return {
    transform: "translateX(".concat(theme.spaces[16], ")"),
    opacity: 0,
    pointerEvents: 'none',
    transitionDelay: '0s',
    zIndex: 0,
    visibility: 'hidden'
  };
}